// src/hooks/useReadjudicacion.js

import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import readjudicacionService from '../services/readjudicacionService';

/**
 * Hook para manejar operaciones de readjudicación masiva
 */
const useReadjudicacion = () => {
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [lotes, setLotes] = useState([]);
    const [totalLotes, setTotalLotes] = useState(0);
    const [loteActual, setLoteActual] = useState(null);
    const [registros, setRegistros] = useState([]);
    const [desapoderados, setDesapoderados] = useState([]);
    const [metricas, setMetricas] = useState(null);

    /**
     * Listar lotes con filtros y paginación
     */
    const listarLotes = useCallback(async (filtros = {}) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.listarLotes(filtros);
            if (response.ok) {
                setLotes(response.lotes || []);
                setTotalLotes(response.total || 0);
                return { lotes: response.lotes, total: response.total };
            } else {
                enqueueSnackbar(response.mensaje || 'Error al listar lotes', { variant: 'error' });
                return { lotes: [], total: 0 };
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al listar lotes';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return { lotes: [], total: 0 };
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener detalle de un lote
     */
    const obtenerLote = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.obtenerLote(idLote);
            if (response.ok) {
                setLoteActual(response.lote);
                return response.lote;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al obtener lote', { variant: 'error' });
                return null;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al obtener lote';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener registros del lote
     */
    const obtenerRegistrosLote = useCallback(async (idLote, tipoOk = null) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.obtenerRegistrosLote(idLote, tipoOk);
            if (response.ok) {
                setRegistros(response.registros || []);
                setDesapoderados(response.desapoderados || []);
                return response.registros;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al obtener registros', { variant: 'error' });
                return [];
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al obtener registros';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return [];
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener métricas del dashboard
     */
    const obtenerMetricas = useCallback(async () => {
        try {
            const response = await readjudicacionService.obtenerMetricas();
            if (response.ok) {
                setMetricas(response.metricas);
                return response.metricas;
            }
            return null;
        } catch (error) {
            console.error('Error al obtener métricas:', error);
            return null;
        }
    }, []);

    /**
     * Auditar un lote
     */
    const auditarLote = useCallback(async (idLote, auditarPares = true) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.auditarLote(idLote, auditarPares);
            if (response.ok) {
                enqueueSnackbar('Lote auditado correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al auditar lote', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al auditar lote';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Editar un registro del lote
     */
    const editarRegistro = useCallback(async (idRegistro, datos) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.editarRegistro(idRegistro, datos);
            if (response.ok) {
                enqueueSnackbar('Registro actualizado correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al editar registro', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al editar registro';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Finalizar auditoría del lote
     */
    const finalizarAuditoria = useCallback(async (idLote, idExpedienteAdm) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.finalizarAuditoria(idLote, idExpedienteAdm);
            if (response.ok) {
                enqueueSnackbar('Auditoría finalizada correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al finalizar auditoría', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al finalizar auditoría';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Eliminar un lote
     */
    const eliminarLote = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.eliminarLote(idLote);
            if (response.ok) {
                enqueueSnackbar('Lote eliminado correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al eliminar lote', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al eliminar lote';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener abogados del lote
     */
    const obtenerAbogadosLote = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.obtenerAbogadosLote(idLote);
            if (response.ok) {
                return response.abogados || [];
            }
            return [];
        } catch (error) {
            console.error('Error al obtener abogados:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Obtener juzgados del lote
     */
    const obtenerJuzgadosLote = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.obtenerJuzgadosLote(idLote);
            if (response.ok) {
                return response.juzgados || [];
            }
            return [];
        } catch (error) {
            console.error('Error al obtener juzgados:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Desapoderar abogados
     */
    const desapoderarAbogados = useCallback(async (idLote, matricula, registrosIds) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.desapoderarAbogados(idLote, matricula, registrosIds);
            if (response.ok) {
                enqueueSnackbar(`${response.cantidad} causa(s) desapoderada(s) correctamente`, { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al desapoderar abogados', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al desapoderar abogados';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Finalizar desapoderamiento
     */
    const finalizarDesapoderamiento = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.finalizarDesapoderamiento(idLote);
            if (response.ok) {
                enqueueSnackbar('Desapoderamiento finalizado correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al finalizar desapoderamiento', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al finalizar desapoderamiento';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Asignar abogados a registros
     */
    const asignarAbogados = useCallback(async (idLote, registrosIds, idAbogado, idCaracter = 3) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.asignarAbogados(idLote, registrosIds, idAbogado, idCaracter);
            if (response.ok) {
                const variant = response.duplicados > 0 ? 'warning' : 'success';
                enqueueSnackbar(response.mensaje || `${response.cantidad} causa(s) asignada(s) correctamente`, { variant });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al asignar abogados', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al asignar abogados';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Finalizar asignación
     */
    const finalizarAsignacion = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.finalizarAsignacion(idLote);
            if (response.ok) {
                enqueueSnackbar('Asignación finalizada correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al finalizar asignación', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al finalizar asignación';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Buscar abogados (para autocomplete)
     */
    const buscarAbogados = useCallback(async (busqueda) => {
        try {
            const response = await readjudicacionService.buscarAbogados(busqueda);
            if (response.ok) {
                return response.abogados || [];
            }
            return [];
        } catch (error) {
            console.error('Error al buscar abogados:', error);
            return [];
        }
    }, []);

    /**
     * Buscar expedientes administrativos (para autocomplete)
     */
    const buscarExpedientesAdm = useCallback(async (busqueda) => {
        try {
            const response = await readjudicacionService.buscarExpedientesAdm(busqueda);
            if (response.ok) {
                return response.expedientes || [];
            }
            return [];
        } catch (error) {
            console.error('Error al buscar expedientes:', error);
            return [];
        }
    }, []);

    /**
     * Obtener registros para desapoderar con filtros
     * No modifica el estado global de registros para no interferir con otros componentes
     */
    const obtenerRegistrosDesapoderar = useCallback(async (idLote, filtros = {}) => {
        try {
            const response = await readjudicacionService.obtenerRegistrosDesapoderar(idLote, filtros);
            if (response.ok) {
                return response.registros || [];
            } else {
                console.error('Error al obtener registros:', response.mensaje);
                return [];
            }
        } catch (error) {
            console.error('Error al obtener registros para desapoderar:', error);
            return [];
        }
    }, []);

    /**
     * Obtener caracteres de abogados
     */
    const obtenerCaracteres = useCallback(async () => {
        try {
            const response = await readjudicacionService.obtenerCaracteres();
            if (response.ok) {
                return response.caracteres || [];
            }
            return [];
        } catch (error) {
            console.error('Error al obtener caracteres:', error);
            return [];
        }
    }, []);

    /**
     * Generar documentos
     */
    const generarDocumento = useCallback(async (idLote, tipoDocumento, configuracion) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.generarDocumento(idLote, tipoDocumento, configuracion);
            if (response.ok) {
                enqueueSnackbar('Documento generado correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al generar documento', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al generar documento';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Generar múltiples documentos
     */
    const generarDocumentos = useCallback(async (idLote, tiposDocumentos, opciones) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.generarDocumentos(idLote, tiposDocumentos, opciones);
            if (response.ok) {
                enqueueSnackbar(`${tiposDocumentos.length} tipo(s) de documento generado(s) correctamente`, { variant: 'success' });
                return response;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al generar documentos', { variant: 'error' });
                return null;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al generar documentos';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Enumerar cartas poder para un lote
     */
    const enumerarCartasPoder = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.enumerarCartasPoder(idLote);
            if (response.ok) {
                enqueueSnackbar(`${response.cantidad} carta(s) poder enumerada(s) correctamente`, { variant: 'success' });
                return response;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al enumerar cartas poder', { variant: 'error' });
                return null;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al enumerar cartas poder';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener datos del Fiscal de Estado
     */
    const obtenerFiscalEstado = useCallback(async () => {
        try {
            const response = await readjudicacionService.obtenerFiscalEstado();
            if (response.ok) {
                return response.fiscalEstado;
            }
            return null;
        } catch (error) {
            console.error('Error al obtener Fiscal de Estado:', error);
            return null;
        }
    }, []);

    /**
     * Obtener registros para el recibo agrupados por abogado
     */
    const obtenerRegistrosRecibo = useCallback(async (idLote) => {
        try {
            const response = await readjudicacionService.obtenerRegistrosRecibo(idLote);
            if (response.ok) {
                return response;
            }
            return null;
        } catch (error) {
            console.error('Error al obtener registros para recibo:', error);
            return null;
        }
    }, []);

    /**
     * Finalizar readjudicación: procesar expedientes e insertar movimientos
     */
    const finalizarReadjudicacion = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.finalizarReadjudicacion(idLote);
            if (response.ok) {
                enqueueSnackbar(
                    `Readjudicación finalizada: ${response.expedientesProcesados} expediente(s) procesado(s)`,
                    { variant: 'success' }
                );
                return response;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al finalizar readjudicación', { variant: 'error' });
                return null;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al finalizar readjudicación';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Actualizar estado del lote
     */
    const actualizarEstadoLote = useCallback(async (idLote, nuevoEstado) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.actualizarEstadoLote(idLote, nuevoEstado);
            if (response.ok) {
                enqueueSnackbar('Estado actualizado correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al actualizar estado', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al actualizar estado';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Pasar/Finalizar readjudicación - Ejecuta el pasaje completo
     */
    const pasarReadjudicacion = useCallback(async (idLote) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.pasarReadjudicacion(idLote);
            if (response.ok) {
                enqueueSnackbar(
                    `Readjudicación finalizada: ${response.expedientesProcesados || 0} expedientes procesados, ${response.movimientosInsertados || 0} movimientos insertados`,
                    { variant: 'success' }
                );
                return response;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al finalizar readjudicación', { variant: 'error' });
                return null;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al finalizar readjudicación';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Excluir un registro
     */
    const excluirRegistro = useCallback(async (idRegistro) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.excluirRegistro(idRegistro);
            if (response.ok) {
                enqueueSnackbar('Registro excluido correctamente', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al excluir registro', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al excluir registro';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Reincluir un registro excluido
     */
    const reincluirRegistro = useCallback(async (idRegistro) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.reincluirRegistro(idRegistro);
            if (response.ok) {
                enqueueSnackbar('Registro reincluido correctamente. Debe auditarse nuevamente.', { variant: 'success' });
                return true;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al reincluir registro', { variant: 'error' });
                return false;
            }
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || error.mensaje || 'Error al reincluir registro';
            enqueueSnackbar(mensaje, { variant: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener documentos generados del lote
     */
    const obtenerDocumentosGenerados = useCallback(async (idLote, tipo = '') => {
        try {
            const response = await readjudicacionService.obtenerDocumentosGenerados(idLote, tipo);
            if (response.ok) {
                return response.documentos || [];
            }
            return [];
        } catch (error) {
            console.error('Error al obtener documentos generados:', error);
            return [];
        }
    }, []);

    // ==================== FIRMA DIGITAL CARTAS PODER ====================

    /**
     * Subir PDF de Carta Poder generado desde el frontend
     */
    const subirPdfCartaPoder = useCallback(async (idEscrito, pdfBlob) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.subirPdfCartaPoder(idEscrito, pdfBlob);
            if (response.ok) {
                enqueueSnackbar('PDF subido correctamente', { variant: 'success' });
                return response;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al subir PDF', { variant: 'error' });
                return null;
            }
        } catch (error) {
            enqueueSnackbar('Error al subir PDF', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Obtener URL para descargar PDF de Carta Poder
     */
    const getUrlDescargaPdf = useCallback((idEscrito, firmado = false) => {
        return readjudicacionService.getUrlDescargaPdf(idEscrito, firmado);
    }, []);

    /**
     * Descargar PDF de Carta Poder como Blob (con auth JWT)
     */
    const descargarPdfBlob = useCallback(async (idEscrito, firmado = false) => {
        return readjudicacionService.descargarPdfBlob(idEscrito, firmado);
    }, []);

    /**
     * Subir PDF firmado de Carta Poder
     */
    const subirPdfFirmado = useCallback(async (idEscrito, pdfFile) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.subirPdfFirmado(idEscrito, pdfFile);
            if (response.ok) {
                enqueueSnackbar('PDF firmado subido correctamente', { variant: 'success' });
                return response;
            } else {
                enqueueSnackbar(response.mensaje || 'Error al subir PDF firmado', { variant: 'error' });
                return null;
            }
        } catch (error) {
            enqueueSnackbar('Error al subir PDF firmado', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    /**
     * Listar Cartas Poder con estado de firma
     */
    const listarCartasPendientesFirma = useCallback(async (idLote = null, soloSinFirmar = false) => {
        setLoading(true);
        try {
            const response = await readjudicacionService.listarCartasPendientesFirma(idLote, soloSinFirmar);
            if (response.ok) {
                return response;
            }
            return null;
        } catch (error) {
            console.error('Error al listar cartas pendientes:', error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Obtener detalle de una Carta Poder específica
     */
    const obtenerCartaPoderDetalle = useCallback(async (idEscrito) => {
        try {
            const response = await readjudicacionService.obtenerCartaPoderDetalle(idEscrito);
            if (response.ok) {
                return response.carta;
            }
            return null;
        } catch (error) {
            console.error('Error al obtener detalle de carta poder:', error);
            return null;
        }
    }, []);

    /**
     * Limpiar estado
     */
    const limpiar = useCallback(() => {
        setLotes([]);
        setLoteActual(null);
        setRegistros([]);
        setDesapoderados([]);
        setMetricas(null);
    }, []);

    return {
        // Estado
        loading,
        lotes,
        totalLotes,
        lote: loteActual,
        loteActual,
        registros,
        desapoderados,
        metricas,

        // Acciones generales
        listarLotes,
        obtenerLote,
        listarRegistros: obtenerRegistrosLote,
        obtenerRegistrosLote,
        obtenerMetricas,
        eliminarLote,

        // Auditoría
        auditarLote,
        editarRegistro,
        finalizarAuditoria,

        // Abogados
        obtenerAbogadosLote,
        obtenerJuzgadosLote,
        desapoderarAbogados,
        finalizarDesapoderamiento,
        asignarAbogados,
        finalizarAsignacion,
        buscarAbogados,
        obtenerRegistrosDesapoderar,
        obtenerCaracteres,

        // Expedientes
        buscarExpedientesAdm,

        // Documentos
        generarDocumento,
        generarDocumentos,
        actualizarEstadoLote,
        pasarReadjudicacion,
        enumerarCartasPoder,
        obtenerFiscalEstado,
        obtenerRegistrosRecibo,
        finalizarReadjudicacion,
        obtenerDocumentosGenerados,

        // Firma Digital Cartas Poder
        subirPdfCartaPoder,
        getUrlDescargaPdf,
        descargarPdfBlob,
        subirPdfFirmado,
        listarCartasPendientesFirma,
        obtenerCartaPoderDetalle,

        // Utilidades
        excluirRegistro,
        reincluirRegistro,
        limpiar
    };
};

export default useReadjudicacion;
